import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import type { TenantDetail, TenantUserDto } from '@order-pilot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { TextField } from '@/components/Form';
import { IconKey, IconLockOpen, IconPlus, IconUsers } from '@/components/Icons';
import { Modal } from '@/components/Modal';
import { SectionCard } from '@/components/SectionCard';
import { EmptyState } from '@/components/States';
import { addTenantUser, resetTenantUserPassword, tenantKeys, unlockTenantUser } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { MIN_PASSWORD_LENGTH, compactErrors, emailError, passwordError, usernameError } from '@/lib/validate';

interface AddUserForm {
  email: string;
  username: string;
  password: string;
}

const INITIAL_ADD_FORM: AddUserForm = { email: '', username: '', password: '' };

export function UsersSection({ tenant }: { tenant: TenantDetail }) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<TenantUserDto | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirmation?: string }>({});
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: (input: { userId: string; password: string }) =>
      resetTenantUserPassword(tenant.id, input.userId, input.password),
    onSuccess: () => {
      setDone(true);
      setPassword('');
      setConfirmation('');
    },
  });

  // Clearing a lockout refetches the tenant so the Locked badge disappears
  // in the same beat — the badge is derived from lockedUntil, so a stale
  // cache would leave the SA staring at an account they just freed.
  const unlockMutation = useMutation({
    mutationFn: (userId: string) => unlockTenantUser(tenant.id, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
    },
  });

  // A tenant may be created with no portal login at all, and gets one added
  // here whenever (and however many times) it actually wants one — this is
  // deliberately repeatable, not a one-shot "first admin" step.
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddUserForm>(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState<Partial<Record<keyof AddUserForm, string>>>({});

  const addMutation = useMutation({
    mutationFn: (input: AddUserForm) => addTenantUser(tenant.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
      setAddOpen(false);
      setAddForm(INITIAL_ADD_FORM);
      setAddErrors({});
    },
  });

  const openAdd = useCallback(() => {
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    addMutation.reset();
    setAddOpen(true);
  }, [addMutation]);

  const closeAdd = useCallback(() => setAddOpen(false), []);

  const handleAddSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const validation = compactErrors<keyof AddUserForm>({
        email: emailError(addForm.email, 'Email'),
        username: usernameError(addForm.username, 'Username'),
        password: passwordError(addForm.password, 'Password'),
      });
      setAddErrors(validation);
      if (Object.keys(validation).length > 0) return;

      addMutation.mutate({
        email: addForm.email.trim(),
        username: addForm.username.trim(),
        password: addForm.password,
      });
    },
    [addForm, addMutation],
  );

  const { reset: resetMutation } = mutation;

  const openFor = useCallback(
    (user: TenantUserDto) => {
      setTarget(user);
      setPassword('');
      setConfirmation('');
      setErrors({});
      setDone(false);
      resetMutation();
    },
    [resetMutation],
  );

  const close = useCallback(() => {
    setTarget(null);
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!target) return;

      const nextErrors: { password?: string; confirmation?: string } = {};
      const invalidPassword = passwordError(password, 'New password');
      if (invalidPassword) nextErrors.password = invalidPassword;
      else if (password !== confirmation) nextErrors.confirmation = 'The two passwords do not match.';
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      mutation.mutate({ userId: target.id, password });
    },
    [confirmation, mutation, password, target],
  );

  return (
    <>
      <SectionCard
        headingId="section-users"
        title="Portal users"
        count={tenant.users.length}
        description="Tenant administrators who sign in to the read-only reports portal."
        action={
          <Button size="sm" variant="primary" icon={<IconPlus className="h-3.5 w-3.5" />} onClick={openAdd}>
            Add user
          </Button>
        }
      >
        {tenant.users.length === 0 ? (
          <EmptyState
            compact
            icon={<IconUsers className="h-5 w-5" />}
            title="No portal users"
            description="This tenant has no portal login yet — it may only need a POS licence. Add one any time."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="th">
                    Email
                  </th>
                  <th scope="col" className="th">
                    Username
                  </th>
                  <th scope="col" className="th">
                    Status
                  </th>
                  <th scope="col" className="th">
                    Created
                  </th>
                  <th scope="col" className="th text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tenant.users.map((user) => (
                  <tr key={user.id}>
                    <td className="td font-medium text-slate-900">{user.email}</td>
                    <td className="td text-slate-500">{user.username}</td>
                    <td className="td">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={user.isActive ? 'success' : 'neutral'} dot>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {/* Locked is orthogonal to Active: an active account
                            can be shut out by the cooldown, which is exactly
                            the case an SA is called about. */}
                        {user.lockedUntil !== null && (
                          <Badge tone="warning" dot>
                            Locked
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="td whitespace-nowrap text-slate-500">{formatDate(user.createdAt)}</td>
                    <td className="td">
                      <div className="flex justify-end gap-2">
                        {user.lockedUntil !== null && (
                          <Button
                            size="sm"
                            icon={<IconLockOpen className="h-3.5 w-3.5" />}
                            loading={unlockMutation.isPending && unlockMutation.variables === user.id}
                            onClick={() => unlockMutation.mutate(user.id)}
                          >
                            Unlock
                          </Button>
                        )}
                        <Button size="sm" icon={<IconKey className="h-3.5 w-3.5" />} onClick={() => openFor(user)}>
                          Reset password
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <Modal
        open={target !== null}
        title="Reset password"
        description={target ? `For ${target.email}` : undefined}
        size="sm"
        onClose={mutation.isPending ? () => undefined : close}
      >
        {done ? (
          <div className="space-y-5">
            <Alert tone="success" title="Password updated">
              Share the new password with the restaurant over a trusted channel. There are no password-reset
              emails yet.
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={close} data-autofocus>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {mutation.isError ? <Alert tone="error">{getErrorMessage(mutation.error)}</Alert> : null}

            <TextField
              id="reset-password"
              label="New password"
              type="password"
              required
              data-autofocus
              autoComplete="new-password"
              hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setErrors({});
              }}
              error={errors.password}
              disabled={mutation.isPending}
            />

            <TextField
              id="reset-password-confirm"
              label="Repeat password"
              type="password"
              required
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
                setErrors({});
              }}
              error={errors.confirmation}
              disabled={mutation.isPending}
            />

            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button onClick={close} disabled={mutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={mutation.isPending}>
                Reset password
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={addOpen}
        title="Add a portal user"
        description={`For ${tenant.name}`}
        size="sm"
        onClose={addMutation.isPending ? () => undefined : closeAdd}
      >
        <form onSubmit={handleAddSubmit} noValidate className="space-y-5">
          {addMutation.isError ? <Alert tone="error">{getErrorMessage(addMutation.error)}</Alert> : null}

          <TextField
            id="add-user-email"
            label="Email"
            type="email"
            required
            data-autofocus
            autoComplete="off"
            spellCheck={false}
            placeholder="owner@bluefig.example"
            value={addForm.email}
            onChange={(event) => {
              setAddForm((current) => ({ ...current, email: event.target.value }));
              setAddErrors((current) => ({ ...current, email: undefined }));
            }}
            error={addErrors.email}
            disabled={addMutation.isPending}
          />

          <TextField
            id="add-user-username"
            label="Username"
            required
            autoComplete="off"
            spellCheck={false}
            placeholder="bluefig"
            value={addForm.username}
            onChange={(event) => {
              setAddForm((current) => ({ ...current, username: event.target.value }));
              setAddErrors((current) => ({ ...current, username: undefined }));
            }}
            error={addErrors.username}
            disabled={addMutation.isPending}
          />

          <TextField
            id="add-user-password"
            label="Password"
            type="password"
            required
            autoComplete="new-password"
            hint={`At least ${MIN_PASSWORD_LENGTH} characters. The restaurant should change it after the first sign-in.`}
            value={addForm.password}
            onChange={(event) => {
              setAddForm((current) => ({ ...current, password: event.target.value }));
              setAddErrors((current) => ({ ...current, password: undefined }));
            }}
            error={addErrors.password}
            disabled={addMutation.isPending}
          />

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button onClick={closeAdd} disabled={addMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={addMutation.isPending}>
              Add user
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
